/**
 * WB AI Chat Utilities
 * Pure utility functions for the chat interface (markdown rendering, escape)
 *
 * Security model: every line is HTML-escaped first, and markdown is applied to the escaped
 * text afterwards. Nothing from the model's output reaches innerHTML unescaped.
 */
/**
 * console.log only when WB_AI_CHAT_DEBUG is enabled (wbAiChatConfig.debug), so production
 * consoles don't show conversation IDs and internal state.
 */
export function debugLog(...args) {
    if (window.wbAiChatConfig?.debug) {
        console.log(...args);
    }
}
/** Link schemes allowed in markdown links. Scheme-less (relative) URLs are also allowed. */
const ALLOWED_LINK_SCHEMES = ['http', 'https', 'mailto', 'tel'];
/**
 * Escape HTML special characters (including quotes, so the result is safe in attributes).
 */
export function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Render a paragraph with proper HTML structure
 */
function renderParagraph(lines) {
    if (!lines || lines.length === 0)
        return '';
    return `<div class="wb-ai-chat-paragraph">${lines.join('\n')}</div>`;
}
/**
 * Normalize content so that literal backslash-n (e.g. from streaming) becomes real newlines.
 * This ensures line-based markdown (headings, lists) is parsed correctly when chunks split on \n.
 */
function normalizeNewlines(content) {
    return content.replace(/\\n/g, '\n');
}
/**
 * Render markdown content (headings, bullet lists, paragraphs, links, bold).
 * The full message (all chunks concatenated) should be passed so that markdown split across chunks renders correctly.
 */
export function renderMarkdown(content) {
    if (!content || typeof content !== 'string') {
        return '';
    }
    content = normalizeNewlines(content);
    const lines = content.split('\n');
    const processedLines = [];
    let inList = false;
    let listItems = [];
    let paragraphLines = [];
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
            if (paragraphLines.length > 0) {
                processedLines.push(renderParagraph(paragraphLines));
                paragraphLines = [];
            }
            if (inList) {
                processedLines.push(renderList(listItems));
                listItems = [];
                inList = false;
            }
            const level = headingMatch[1].length;
            const title = headingMatch[2];
            processedLines.push(`<h${level} class="wb-ai-chat-heading wb-ai-chat-heading-${level}">${processInlineElements(title)}</h${level}>`);
            continue;
        }
        const bulletMatch = line.match(/^[-*]\s+(.+)$/);
        if (bulletMatch) {
            if (paragraphLines.length > 0) {
                processedLines.push(renderParagraph(paragraphLines));
                paragraphLines = [];
            }
            if (!inList) {
                inList = true;
                listItems = [];
            }
            listItems.push(bulletMatch[1]);
            continue;
        }
        if (inList) {
            processedLines.push(renderList(listItems));
            listItems = [];
            inList = false;
        }
        if (line.trim() === '') {
            if (paragraphLines.length > 0) {
                processedLines.push(renderParagraph(paragraphLines));
                paragraphLines = [];
            }
        }
        else {
            paragraphLines.push(processInlineElements(line));
        }
    }
    if (paragraphLines.length > 0) {
        processedLines.push(renderParagraph(paragraphLines));
    }
    if (inList) {
        processedLines.push(renderList(listItems));
    }
    return processedLines.join('\n');
}
/**
 * Apply bold markdown (** and __) to already-escaped text.
 */
function applyBold(escaped) {
    return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong class="wb-ai-chat-bold">$1</strong>')
        .replace(/__(.+?)__/g, '<strong class="wb-ai-chat-bold">$1</strong>');
}
/**
 * Whether a link URL is safe to put in an href (allowed scheme, or relative).
 *
 * @param url Raw (unescaped) URL from the markdown link.
 */
function isSafeLinkUrl(url) {
    // Browsers ignore whitespace/control characters inside a scheme ("java\tscript:").
    const compact = url.replace(/[\u0000- ]/g, '');
    const scheme = compact.match(/^([a-z][a-z0-9+.-]*):/i);
    return !scheme || ALLOWED_LINK_SCHEMES.includes(scheme[1].toLowerCase());
}
/**
 * Process inline markdown: links [text](url) and bold (** / __). Input is raw text; output is safe HTML.
 */
function processInlineElements(text) {
    if (!text)
        return '';
    // Links are swapped for placeholders so the bold pass can't reach into href attributes.
    // NUL is stripped from the input so it can't collide with the placeholder delimiters.
    const links = [];
    let html = escapeHtml(text.replace(/\u0000/g, ''));
    html = html.replace(/\[([^\]]*)\]\(([^)]*)\)/g, (_match, linkText, escapedUrl) => {
        const url = escapedUrl.trim();
        const label = applyBold(linkText);
        // Unescape only to validate the scheme; the escaped form is what goes into the attribute.
        const rawUrl = url.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
        const anchor = url !== '' && isSafeLinkUrl(rawUrl)
            ? `<a href="${url}" target="_blank" rel="noopener noreferrer" class="wb-ai-chat-link">${label}</a>`
            : label;
        links.push(anchor);
        return `\u0000${links.length - 1}\u0000`;
    });
    html = applyBold(html);
    return html.replace(/\u0000(\d+)\u0000/g, (_m, i) => links[Number(i)]);
}
/**
 * Render a list with processed inline elements
 */
function renderList(items) {
    if (!items || items.length === 0)
        return '';
    const listItems = items
        .map((item) => `<li class="wb-ai-chat-list-item">${processInlineElements(item)}</li>`)
        .join('');
    return `<ul class="wb-ai-chat-list">${listItems}</ul>`;
}
